'use client';
import React from 'react';

import { Breadcrumbs } from '../../../../components/breadcrumbs';
import { PageComponentImage } from '../../../../components/page-components/image/page-component-image';
import { PageComponentMain } from '../../../../components/page-components/main/page-component-main';
import useGetMobileAppWebViewHeader from '../../../../hooks/use-get-mobile-app-web-view';
import { IContentfulImage } from '../../../../interfaces/contentful-image.interface';
import { IMeetingPlacePageNewsItem } from '@/lib/fragments/meetingPlacePageNewsItem';
import { IMeetingplacePageHomeMinifiedExtendedLogo } from '@/lib/fragments/meetingPlacePageHome-minified';
import { IGtmParameters, useSendGtmEvent } from '@/hooks/use-send-gtm-event';
import { GtmPageView } from '@/components/page-components/analytics/gtm-page-view';
import { useUser } from '@/providers/UserProvider';
import { useIsMPLivliFamily } from '@/providers/ConfigProvider';

interface MeetingPlacePageNewsItemProps {
  metaData: any;
  node_locale: string;
  newsData: IMeetingPlacePageNewsItem | null;
  homeData: IMeetingplacePageHomeMinifiedExtendedLogo | null;
  parent?: any;
}

export const MeetingPlacePageNewsItem = ({
  metaData,
  node_locale,
  newsData,
  homeData,
  parent,
}: MeetingPlacePageNewsItemProps) => {
  const { user } = useUser();
  const isLivliFamily = useIsMPLivliFamily();
  const page = newsData;
  const homepage = homeData;
  const pageName = page?.name ? page.name : '';
  const image = page?.coverImage ? page.coverImage : undefined;

  let logo: IContentfulImage | undefined = undefined;
  if (page?.tenant) {
    logo = page?.tenant?.logo
      ? page.tenant.logo
      : page?.tenant?.brand
        ? page.tenant.brand?.logo
        : undefined;
  }

  const gtmParams: IGtmParameters = {
    event: 'view_news_detail',
    detail_name: pageName,
    contentful_id: page?.sys?.id || '',
    offer_type: 'Offer Type Not Available',
    user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
  };
  useSendGtmEvent(gtmParams);

  const isWebView = useGetMobileAppWebViewHeader();

  return (
    <>
      {page && (
        <>
          {/* ✅ GTM PAGE VIEW */}
          <GtmPageView
            homepage={homepage}
            language={node_locale}
            pageTitle={page?.name ?? ''}
          />

          <main className="bg-background-primary-light px-6 lg:-ml-22 lg:bg-transparent lg:px-0">
            <Breadcrumbs
              breadcrumbs={[pageName]}
              homeText={homepage?.breadcrumbHomeText ?? ''}
              cssClass={`lg:ml-22 ${isWebView ? '!pt-8' : ''}`}
              node_locale={node_locale}
              parent={parent}
            />
            <div className="grid grid-cols-11 gap-6 pt-5">
              <div className="col-span-12 bg-background-primary-light lg:col-span-5 lg:bg-transparent">
                <PageComponentImage
                  image={image}
                  stickyImage={true}
                  header={pageName}
                  date={page?.date}
                  isdatevisible={page?.isdatevisible}
                  hasShadedOverlay={true}
                  node_locale={node_locale}
                />
              </div>
              <div className="relative col-span-12 lg:col-span-6">
                <PageComponentMain
                  header={pageName}
                  caption={page?.caption}
                  richText={page?.body}
                  logo={logo}
                  node_locale={node_locale}
                  showSocialShare={
                    homepage?.meetingPlace?.showSocialShare ?? false
                  }
                />
              </div>
            </div>
          </main>
        </>
      )}
    </>
  );
};

export default MeetingPlacePageNewsItem;
